import { createDraftSafeSelector } from '@reduxjs/toolkit'
import { OptionChainViewKey, OptionChainViewType } from '../assets/constants'
import { Security } from '../proto/generated/security'
import { RootState } from '../store'
import { WidgetDashboardConfig } from '../components/widgetDashboard/widgetDashboard.interface'
import { TimeBucket } from '../proto/generated/common'
import { PrimarySecurity } from '../util/security'
import { timeIntervalValues } from '../util/tradePage'

export type { PrimarySecurity } from '../util/security'

const state = (state: RootState) => state

const buildSkeletonPrimarySecurity = (primarySecurity: RootState['homeStore']['primarySecurity']): PrimarySecurity => ({
    key: primarySecurity.key,
    displayName: primarySecurity.displayName,
    expressionMode: primarySecurity.expressionMode,
    expressionSecurities: [],
    underlyingSecurityCode: primarySecurity.underlyingSecurityCode
})

export const selectPrimarySecurity = createDraftSafeSelector(state, (state: RootState): PrimarySecurity => {
    const { resolvedPrimarySecurity, primarySecurity } = state.homeStore
    return resolvedPrimarySecurity ?? buildSkeletonPrimarySecurity(primarySecurity)
})

export const selectUnderlyingSecurity = createDraftSafeSelector(state, (state: RootState): any => {
    const { key, expressionMode, underlyingSecurityCode, securities: stateSecurities } = state.homeStore.primarySecurity
    const resolved = state.homeStore.resolvedPrimarySecurity
    const securities: Security[] = underlyingSecurityCode
        ? (resolved?.underlying ? [resolved.underlying] : [])
        : (resolved?.expressionSecurities ?? [])
    return {
        key,
        expressionMode,
        tokens: underlyingSecurityCode ? [underlyingSecurityCode] : stateSecurities,
        securities
    }
})

export const defaultOptionChainColumns = [
    'bid',
    'ask',
    'ltp',
    'openInterest',
    'oiChange|outlook',
    'volume',
    'impliedVolatility',
    'delta',
    'gamma',
    'vega',
    'theta',
    'rho',
]

export const defaultExtendedOptionChainColumns = ['ltp', 'impliedVolatility', 'delta', 'openInterest']

export const selectOptionChainView = (viewKey: OptionChainViewKey) => createDraftSafeSelector(state, (state: RootState): OptionChainViewType => {
    return state.homeStore.optionChain[viewKey].view
})

export const selectOptionChainMirrorScroll = (viewKey: OptionChainViewKey, type: 'extended' | 'normal') => createDraftSafeSelector(state, (state: RootState) => {
    return state.commonStore.optionChain?.[viewKey]?.[type]?.isMirrorScroll as boolean
})

export const selectExpandedRows = (viewKey: OptionChainViewKey) => createDraftSafeSelector(state, (state: RootState): string[] => {
    return state.homeStore.optionChain[viewKey].expandedRows
})

export const checkExpandedRowsContainRow = (key: string, viewKey: OptionChainViewKey) => createDraftSafeSelector(state, (state: RootState): boolean => {
    return state.homeStore.optionChain[viewKey].expandedRows.includes(key)
})

export const selectOptionChainTimeInterval = (viewKey: OptionChainViewKey, type: 'extended' | 'normal') => createDraftSafeSelector(state, (state: RootState): TimeBucket => {
    return (state.commonStore.optionChain?.[viewKey]?.[type]?.timeInterval) ?? (timeIntervalValues[timeIntervalValues.length - 1].value)
})

export const selectOptionChainColumns = (viewKey: OptionChainViewKey, type: 'extended' | 'normal') => createDraftSafeSelector(state, (state: RootState): string[] => {
    return state.commonStore.optionChain?.[viewKey]?.[type]?.optionSelectedColumns || (type === 'extended' ? defaultExtendedOptionChainColumns : defaultOptionChainColumns)
})

export const selectOptionsExpiry = createDraftSafeSelector(state, (state: RootState): string => {
    return state.homeStore.fnoView.selectedOptionsExpiry
})

export const selectActiveLayout = (store: RootState) => store.widgetDashboardStore.layouts[store.homeStore.activeLayout] as WidgetDashboardConfig

export const selectActiveLayoutId = (store: RootState) => store.homeStore.activeLayout as string

export const selectLastActiveLayoutId = (store: RootState) => store.homeStore.lastActiveLayout as string

export const selectSecuritySearchOpen = (store: RootState) => store.homeStore.securitySearchOpen

export const selectIsPrimarySecurityLoading = (store: RootState) =>
    store.homeStore.resolvedPrimarySecurity === null && !!store.homeStore.primarySecurity?.key